import React, { useCallback, useEffect, useState } from 'react';
import { Link2, Megaphone, BarChart3, X, Loader2 } from 'lucide-react';
import type { QueryParams } from '@yawi/domain';
import { useServices, useToast } from '@/app/providers';
import { AsyncSearchableSelect, type SelectOption } from '@/core/ui/components';

interface LinkedItem {
  id: string;
  title: string;
}

interface ProjectLinksSectionProps {
  projectId: string;
  canManage: boolean;
}

/**
 * Vincula el proyecto con comunicados y encuestas ya existentes.
 *
 * El backend solo guarda y devuelve IDs — no duplica el contenido ni acopla el
 * módulo Transparency con Communication. Los títulos se resuelven acá con los
 * repositorios que la app ya tiene. Son pocos por proyecto, así que resolverlos
 * de a uno es más simple que un endpoint de resolución masiva.
 */
export const ProjectLinksSection: React.FC<ProjectLinksSectionProps> = ({
  projectId,
  canManage,
}) => {
  const { transparencyProjectRepo, announcementRepo, pollRepo } = useServices();
  const toast = useToast();

  const [announcements, setAnnouncements] = useState<LinkedItem[]>([]);
  const [polls, setPolls] = useState<LinkedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const loadLinks = useCallback(async () => {
    try {
      const { announcementIds, pollIds } = await transparencyProjectRepo.getLinks(projectId);

      const [resolvedAnnouncements, resolvedPolls] = await Promise.all([
        Promise.all(
          announcementIds.map(async (id) => {
            const a = await announcementRepo.getAnnouncementById(id).catch(() => null);
            // Si el comunicado fue borrado, se muestra el vínculo huérfano en
            // vez de ocultarlo: así el admin puede desvincularlo.
            return { id, title: a?.title ?? 'Comunicado no disponible' };
          })
        ),
        Promise.all(
          pollIds.map(async (id) => {
            const p = await pollRepo.getPollById(id).catch(() => null);
            return { id, title: p?.title ?? 'Encuesta no disponible' };
          })
        ),
      ]);

      setAnnouncements(resolvedAnnouncements);
      setPolls(resolvedPolls);
    } catch {
      toast.error('Error al cargar los vínculos');
    } finally {
      setLoading(false);
    }
  }, [transparencyProjectRepo, announcementRepo, pollRepo, projectId, toast]);

  useEffect(() => {
    loadLinks();
  }, [loadLinks]);

  const loadAnnouncementOptions = useCallback(
    async (inputValue: string): Promise<SelectOption[]> => {
      const params: QueryParams = { page: 1, limit: 10 };
      if (inputValue) params.search = inputValue;
      const response = await announcementRepo.getAnnouncements(params);
      return response.data
        .filter((a) => !announcements.some((linked) => linked.id === a.id))
        .map((a) => ({ value: a.id, label: a.title }));
    },
    [announcementRepo, announcements]
  );

  const loadPollOptions = useCallback(
    async (inputValue: string): Promise<SelectOption[]> => {
      const params: QueryParams = { page: 1, limit: 10 };
      if (inputValue) params.search = inputValue;
      const response = await pollRepo.getPolls(params);
      return response.data
        .filter((p) => !polls.some((linked) => linked.id === p.id))
        .map((p) => ({ value: p.id, label: p.title }));
    },
    [pollRepo, polls]
  );

  const handleLinkAnnouncement = async (id: string | null) => {
    if (!id) return;
    setBusy(true);
    try {
      await transparencyProjectRepo.linkAnnouncement(projectId, id);
      toast.success('Comunicado vinculado');
      await loadLinks();
    } catch {
      toast.error('No se pudo vincular el comunicado');
    } finally {
      setBusy(false);
    }
  };

  const handleUnlinkAnnouncement = async (id: string) => {
    setBusy(true);
    try {
      await transparencyProjectRepo.unlinkAnnouncement(projectId, id);
      await loadLinks();
    } catch {
      toast.error('No se pudo desvincular el comunicado');
    } finally {
      setBusy(false);
    }
  };

  const handleLinkPoll = async (id: string | null) => {
    if (!id) return;
    setBusy(true);
    try {
      await transparencyProjectRepo.linkSurvey(projectId, id);
      toast.success('Encuesta vinculada');
      await loadLinks();
    } catch {
      toast.error('No se pudo vincular la encuesta');
    } finally {
      setBusy(false);
    }
  };

  const handleUnlinkPoll = async (id: string) => {
    setBusy(true);
    try {
      await transparencyProjectRepo.unlinkSurvey(projectId, id);
      await loadLinks();
    } catch {
      toast.error('No se pudo desvincular la encuesta');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-400">
        <Loader2 size={14} className="animate-spin" /> Cargando vínculos...
      </div>
    );
  }

  const renderChips = (
    items: LinkedItem[],
    icon: React.ReactNode,
    onUnlink: (id: string) => void,
    emptyLabel: string
  ) =>
    items.length === 0 ? (
      <p className="text-sm text-slate-400">{emptyLabel}</p>
    ) : (
      <div className="flex flex-wrap gap-2">
        {items.map((item) => (
          <span
            key={item.id}
            className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-lg bg-slate-50 border border-slate-200 text-sm text-slate-700"
          >
            {icon}
            <span className="max-w-[240px] truncate">{item.title}</span>
            {canManage && (
              <button
                type="button"
                onClick={() => onUnlink(item.id)}
                disabled={busy}
                aria-label={`Desvincular ${item.title}`}
                className="text-slate-300 hover:text-red-500 transition-colors disabled:opacity-50"
              >
                <X size={13} />
              </button>
            )}
          </span>
        ))}
      </div>
    );

  return (
    <div className="space-y-4">
      <h4 className="font-semibold text-slate-800 flex items-center gap-2">
        <Link2 size={16} /> Comunicados y encuestas relacionados
      </h4>

      <div className="space-y-2">
        <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Comunicados</p>
        {renderChips(
          announcements,
          <Megaphone size={12} className="text-amber-500 shrink-0" />,
          handleUnlinkAnnouncement,
          'Sin comunicados vinculados.'
        )}
        {canManage && (
          <AsyncSearchableSelect
            value={null}
            onChange={handleLinkAnnouncement}
            loadOptions={loadAnnouncementOptions}
            placeholder="Buscar un comunicado para vincular..."
            defaultOptions
            minLength={0}
            isDisabled={busy}
            noOptionsMessage="No hay comunicados disponibles"
          />
        )}
      </div>

      <div className="space-y-2">
        <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Encuestas</p>
        {renderChips(
          polls,
          <BarChart3 size={12} className="text-violet-500 shrink-0" />,
          handleUnlinkPoll,
          'Sin encuestas vinculadas.'
        )}
        {canManage && (
          <AsyncSearchableSelect
            value={null}
            onChange={handleLinkPoll}
            loadOptions={loadPollOptions}
            placeholder="Buscar una encuesta para vincular..."
            defaultOptions
            minLength={0}
            isDisabled={busy}
            noOptionsMessage="No hay encuestas disponibles"
          />
        )}
      </div>
    </div>
  );
};
